// Org-level follow-up reminder settings. The numbers feed directly into the
// reminder engine (ReminderOverlay): beforeGapMinutes / afterGapMinutes
// define how early/late a scheduled time can fire, intervalMinutes is the
// snooze step. `enabled=false` makes /follow-ups/active return [] so the
// engine no-ops without code changes here.

import React, { useEffect, useState } from "react";
import { Bell, Loader2, AlertCircle, Save } from "lucide-react";
import toast from "react-hot-toast";
import { Button, Input, Label } from "../../atoms";
import {
  useFollowUpSettings,
  useUpdateFollowUpSettings,
} from "../../hooks/useFollowUps";

function formatErr(err) {
  return err?.response?.data?.error || err?.message || "Request failed";
}

const FIELDS = [
  { key: "beforeGapMinutes", label: "Remind before (minutes)", min: 5, max: 1440,
    hint: "How long before the scheduled time the reminder starts firing." },
  { key: "afterGapMinutes", label: "Keep firing after (minutes)", min: 5, max: 1440,
    hint: "Window after scheduled time during which a missed reminder still pops." },
  { key: "intervalMinutes", label: "Snooze step (minutes)", min: 1, max: 240,
    hint: "How long the Snooze button pauses a reminder. Also the minimum gap between re-fires." },
];

function Toggle({ checked, onChange, disabled, label, hint }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0" }}>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        disabled={disabled}
        style={{
          width: 40,
          height: 22,
          flexShrink: 0,
          borderRadius: 999,
          border: "none",
          background: checked ? "var(--crm-primary)" : "var(--color-border-subtle)",
          cursor: disabled ? "not-allowed" : "pointer",
          position: "relative",
          transition: "background 120ms ease",
          opacity: disabled ? 0.5 : 1,
        }}
        aria-pressed={checked}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 20 : 2,
            width: 18,
            height: 18,
            borderRadius: 999,
            background: "#fff",
            transition: "left 120ms ease",
          }}
        />
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
        {hint && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>{hint}</div>
        )}
      </div>
    </div>
  );
}

function NumberField({ field, value, onChange, disabled }) {
  return (
    <div>
      <Label>{field.label}</Label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={field.min}
        max={field.max}
        disabled={disabled}
      />
      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>
        {field.hint} <span style={{ opacity: 0.7 }}>({field.min}–{field.max})</span>
      </div>
    </div>
  );
}

export default function FollowUpSettingsSection() {
  const { data, isLoading, isError, error } = useFollowUpSettings();
  const update = useUpdateFollowUpSettings();

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (data?.settings) {
      setForm({
        enabled: data.settings.enabled,
        audibleChime: data.settings.audibleChime,
        beforeGapMinutes: String(data.settings.beforeGapMinutes),
        afterGapMinutes: String(data.settings.afterGapMinutes),
        intervalMinutes: String(data.settings.intervalMinutes),
      });
    }
  }, [data?.settings]);

  const dirty = form && data?.settings && (
    form.enabled !== data.settings.enabled ||
    form.audibleChime !== data.settings.audibleChime ||
    Number(form.beforeGapMinutes) !== data.settings.beforeGapMinutes ||
    Number(form.afterGapMinutes) !== data.settings.afterGapMinutes ||
    Number(form.intervalMinutes) !== data.settings.intervalMinutes
  );

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        enabled: form.enabled,
        audibleChime: form.audibleChime,
        beforeGapMinutes: Number(form.beforeGapMinutes),
        afterGapMinutes: Number(form.afterGapMinutes),
        intervalMinutes: Number(form.intervalMinutes),
      });
      toast.success("Follow-up settings updated");
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <header>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: "var(--crm-primary)" }}>
          <Bell size={12} /> Follow-ups
        </div>
        <h2 style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em" }}>
          Reminder behavior
        </h2>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>
          Tune when reminders pop and how long Snooze pauses them. Changes affect every user in this org.
        </p>
      </header>

      {isError && (
        <div style={{
          fontSize: 12, color: "var(--crm-danger)",
          display: "flex", alignItems: "center", gap: 6,
          padding: 10, borderRadius: 8, background: "var(--crm-danger-soft)",
        }}>
          <AlertCircle size={13} /> {formatErr(error)}
        </div>
      )}

      {isLoading || !form ? (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
          <Loader2 size={14} className="animate-spin" /> Loading settings…
        </div>
      ) : (
        <div style={{
          padding: 16, borderRadius: 8,
          background: "var(--color-bg-surface)",
          border: "1px solid var(--color-border-subtle)",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <Toggle
            checked={form.enabled}
            onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
            disabled={update.isPending}
            label="Enable reminder popups"
            hint="When off, scheduled follow-ups stay on leads but no popup or chime fires."
          />
          <div style={{ borderTop: "1px solid var(--color-border-subtle)" }} />
          <Toggle
            checked={form.audibleChime}
            onChange={(v) => setForm((f) => ({ ...f, audibleChime: v }))}
            disabled={update.isPending || !form.enabled}
            label="Play chime"
            hint="Loops a short tone while the popup is open."
          />
          <div style={{ borderTop: "1px solid var(--color-border-subtle)", margin: "4px 0 8px" }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {FIELDS.map((f) => (
              <NumberField
                key={f.key}
                field={f}
                value={form[f.key]}
                onChange={(v) => setForm((prev) => ({ ...prev, [f.key]: v }))}
                disabled={update.isPending || !form.enabled}
              />
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, marginTop: 8, paddingTop: 12, borderTop: "1px solid var(--color-border-subtle)" }}>
            <Button
              variant="primary"
              size="sm"
              leftIcon={update.isPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              onClick={handleSave}
              disabled={!dirty || update.isPending}
            >
              {update.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
